// Left rail of the Product Attributes dashboard: selectable list of attribute
// types. Owns all type-level CRUD modals (create/edit/category-bindings/
// delete) — the screen only supplies the "+ Type" trigger state, since that
// button lives in the shared header alongside the page stats.

import { Icon } from '@chakra-ui/react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButtonWithTooltip,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  Tooltip,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from '@elkaso-app/web-design';
import { useGetDealCategoryTreeApi } from 'apis/exclusive-deals';
import { AttributeTypeAdmin, useDeleteAttributeTypeApi, useUpdateAttributeTypeApi } from 'apis/product-attributes';
import { findCategoryPath } from 'pages/exclusive-deals/components/editable-deals-grid/category-tree-helpers';
import { useState } from 'react';
import { FiEdit, FiFolder, FiTrash2 } from 'react-icons/fi';

import { CategoryBindingsModal } from './category-bindings-modal';
import { TypeFormModal } from './type-form-modal';

interface Props {
  types: AttributeTypeAdmin[];
  isLoading: boolean;
  selectedTypeId: number | null;
  onSelect: (id: number) => void;
  /**
   * Session cache of type-bindings, keyed by type id — see
   * product-attributes-screen.tsx. Overrides `type.boundCategoryIds` when set,
   * since it's written by the bindings modal on load/save and is therefore
   * fresher than a possibly-stale types list.
   */
  typeCategoryCache: Record<number, number[]>;
  onTypeBindingsLoaded: (typeId: number, ids: number[]) => void;
  isCreateOpen: boolean;
  onCloseCreate: () => void;
  /**
   * Ids of types with at least one value matching the active search query
   * (see attribute-search.ts). `undefined` while not searching — every type
   * renders at full opacity then, same as before search existed.
   */
  matchingTypeIds?: Set<number>;
}

export const AttributeTypesRail = ({
  types,
  isLoading,
  selectedTypeId,
  onSelect,
  typeCategoryCache,
  onTypeBindingsLoaded,
  isCreateOpen,
  onCloseCreate,
  matchingTypeIds,
}: Props) => {
  const toast = useToast();
  const [editType, setEditType] = useState<AttributeTypeAdmin | null>(null);
  const [categoriesType, setCategoriesType] = useState<AttributeTypeAdmin | null>(null);
  const [deleteType, setDeleteType] = useState<AttributeTypeAdmin | null>(null);

  const { data: treeData } = useGetDealCategoryTreeApi();
  const categoryTree = treeData?.data ?? [];

  const updateApi = useUpdateAttributeTypeApi({
    onError: (error: any) => {
      // hooks toast server messages; this is the network-error fallback
      if (!error?.response?.data?.message) toast({ title: 'Failed to update attribute type', status: 'error' });
    },
  });
  const deleteApi = useDeleteAttributeTypeApi({
    onSuccess: () => {
      toast({ title: `Attribute type "${deleteType?.label}" deleted`, status: 'success' });
      setDeleteType(null);
    },
    onError: (error: any) => {
      // hooks toast server messages; this is the network-error fallback
      if (!error?.response?.data?.message) toast({ title: 'Failed to delete attribute type.', status: 'error' });
    },
  });

  const toggleActive = (type: AttributeTypeAdmin, isActive: boolean) =>
    updateApi.mutate({ id: type.id, data: { isActive } });
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Box w='280px' flexShrink={0}>
      {isLoading ? (
        <Flex justify='center' py='8'>
          <Spinner size='md' color='gray.400' />
        </Flex>
      ) : (
        <VStack align='stretch' spacing='2'>
          {types.map((type) => {
            // Chips render up front from the list payload; the cache override
            // and the button fallback (older BE without boundCategoryIds) keep
            // the modal round-trip and deploy gap covered.
            const cached = typeCategoryCache[type.id] ?? type.boundCategoryIds;
            const selected = type.id === selectedTypeId;
            const isMatch = matchingTypeIds?.has(type.id) ?? true;

            return (
              <Box
                key={type.id}
                borderWidth='1px'
                borderRadius='md'
                borderColor={selected ? 'red.300' : matchingTypeIds && isMatch ? 'purple.200' : 'gray.200'}
                bg={selected ? 'red.50' : 'white'}
                opacity={isMatch ? 1 : 0.4}
                px='3'
                py='2'
                cursor='pointer'
                onClick={() => onSelect(type.id)}
                _hover={{ borderColor: 'red.300' }}>
                <Flex align='center' justify='space-between' gap='2'>
                  <Text fontWeight='medium' fontSize='sm' noOfLines={1}>
                    {type.label}
                  </Text>
                  <Box onClick={stop}>
                    <Switch
                      size='sm'
                      isChecked={type.isActive}
                      onChange={(e) => toggleActive(type, e.target.checked)}
                      colorScheme='green'
                    />
                  </Box>
                </Flex>

                <HStack mt='1' spacing='2'>
                  <Text fontSize='xs' color='gray.500' fontFamily='mono'>
                    {type.key}
                  </Text>
                  <Badge fontSize='2xs' colorScheme='gray'>
                    {type.values.length} value{type.values.length === 1 ? '' : 's'}
                  </Badge>
                </HStack>

                <Flex mt='2' align='center' justify='space-between' gap='2' onClick={stop}>
                  {cached === undefined ? (
                    <Button
                      size='xs'
                      variant='outline'
                      leftIcon={<Icon as={FiFolder} />}
                      onClick={() => setCategoriesType(type)}>
                      Categories
                    </Button>
                  ) : cached.length === 0 ? (
                    <Badge colorScheme='gray' cursor='pointer' onClick={() => setCategoriesType(type)}>
                      All categories
                    </Badge>
                  ) : (
                    <Wrap spacing='1' cursor='pointer' onClick={() => setCategoriesType(type)}>
                      {cached.map((id) => {
                        const path = findCategoryPath(categoryTree, id);
                        const leaf = path.length ? path[path.length - 1].title || `#${id}` : `#${id}`;
                        const breadcrumb = path.length
                          ? path.map((p) => p.title || `Untitled (${p.id})`).join(' › ')
                          : `Category #${id}`;
                        return (
                          <WrapItem key={id}>
                            <Tooltip label={breadcrumb}>
                              <Badge colorScheme='red' variant='subtle' fontSize='2xs'>
                                {leaf}
                              </Badge>
                            </Tooltip>
                          </WrapItem>
                        );
                      })}
                    </Wrap>
                  )}

                  <HStack spacing='0' flexShrink={0}>
                    <IconButtonWithTooltip
                      size='xs'
                      label='Edit'
                      aria-label='edit-type-btn'
                      Icon={<Icon as={FiEdit} />}
                      variant='unstyled'
                      onClick={() => setEditType(type)}
                    />
                    <IconButtonWithTooltip
                      size='xs'
                      label='Delete'
                      aria-label='delete-type-btn'
                      Icon={<Icon as={FiTrash2} />}
                      variant='unstyled'
                      onClick={() => setDeleteType(type)}
                    />
                  </HStack>
                </Flex>
              </Box>
            );
          })}

          {types.length === 0 && (
            <Text color='gray.500' fontSize='sm' textAlign='center' py='4'>
              No attribute types yet.
            </Text>
          )}
        </VStack>
      )}

      <TypeFormModal isOpen={isCreateOpen} onClose={onCloseCreate} />
      {editType && <TypeFormModal isOpen={!!editType} onClose={() => setEditType(null)} type={editType} />}
      {categoriesType && (
        <CategoryBindingsModal
          isOpen={!!categoriesType}
          onClose={() => setCategoriesType(null)}
          kind='type'
          targetId={categoriesType.id}
          targetLabel={categoriesType.label}
          onBindingsLoaded={(ids) => onTypeBindingsLoaded(categoriesType.id, ids)}
        />
      )}

      <Modal isOpen={!!deleteType} onClose={() => setDeleteType(null)} size='md' closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Attribute Type</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status='warning'>
              <AlertIcon />
              This action cannot be undone. Are you sure you want to delete “{deleteType?.label}”?
            </Alert>
          </ModalBody>
          <ModalFooter gap='2'>
            <Button variant='outline' onClick={() => setDeleteType(null)} isDisabled={deleteApi.isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={() => deleteType && deleteApi.mutate({ id: deleteType.id })}
              isLoading={deleteApi.isLoading}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
